/**
 * MBD Studio theme — tokens borrowed from console.mbd.xyz + getembed.ai
 * (https://getembed.ai uses DM Sans; mbd console uses the same hot-pink
 * #E01A96 primary, dark-on-light neutrals, near-white surfaces).
 *
 * Hex equivalents converted from the canonical `oklch(...)` palette in
 * `dashboard-frontend/src/styles/globals.css`.
 */
import { createTheme } from '@mui/material/styles';

export const palette = {
  brandPink: '#E01A96',         // oklch(0.6277 0.2573 342.73)
  brandPinkHover: '#C71785',
  brandPinkSoft: '#FCE9F2',
  textPrimary: '#1A1A1F',       // oklch(0.141 0.005 285.823)
  textSecondary: '#74747D',     // oklch(0.552 0.016 285.938)
  border: '#E9E9EC',            // oklch(0.92 0.004 286.32)
  surfaceMuted: '#F5F5F7',      // oklch(0.967 0.001 286.375)
  surfaceMutedFg: '#272730',    // oklch(0.21 0.006 285.885)
  appBarBg: '#1A1A1F',          // dark header to match console aesthetic
  appBarText: '#FAFAFA',
  error: '#DD4040',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: palette.brandPink,
      dark: palette.brandPinkHover,
      light: palette.brandPinkSoft,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: palette.surfaceMutedFg,
      contrastText: '#FFFFFF',
    },
    error: { main: palette.error },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: palette.textPrimary,
      secondary: palette.textSecondary,
    },
    divider: palette.border,
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    button: { textTransform: 'none', fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.appBarBg,
          color: palette.appBarText,
          boxShadow: 'none',
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
        colorPrimary: {
          backgroundColor: palette.brandPinkSoft,
          color: palette.brandPinkHover,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: palette.border,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, minHeight: 36 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: palette.border },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: palette.textSecondary,
            fontSize: 12,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            backgroundColor: palette.surfaceMuted,
          },
        },
      },
    },
  },
});

export default theme;
